'use client';

import { useState } from 'react';
import {
  ArrowLeft,
  Check,
  Copy,
  Expand,
  EyeOff,
  Heart,
  Loader2,
  Music,
  Play,
  RefreshCw,
  Share2,
  User,
} from 'lucide-react';
import { ImagePreviewModal } from './image-preview-modal';
import { S3Image } from './s3-image';
import { VideoPlayer } from './video-player';
import { cn } from '../utils/cn';

export interface FeedDetailItem {
  id: string;
  resultUrl: string | null;
  thumbnailUrl: string | null;
  audioTracks?: Array<{
    id: string;
    audioUrl: string;
    imageUrl?: string | null;
    title?: string | null;
    tags?: string | null;
    duration?: number | null;
  }>;
  resultType: 'IMAGE' | 'VIDEO' | 'AUDIO';
  prompt: string | null;
  negativePrompt?: string | null;
  likesCount: number;
  createdAt: string;
  isPromptPrivate?: boolean;
  model: {
    name: string;
    slug: string;
  };
  user: {
    username: string | null;
    firstName: string | null;
  };
  trend?: {
    id: string;
  } | null;
}

interface FeedDetailScreenProps {
  feedItem?: FeedDetailItem | null;
  isLoading?: boolean;
  hasError?: boolean;
  isLiking?: boolean;
  copied?: boolean;
  onBack: () => void;
  onOpenProfile: () => void;
  onLike: () => void;
  onShare: () => void;
  onRepeat: () => void;
  onCopyPrompt: () => void;
}

export function FeedDetailScreen({
  feedItem,
  isLoading = false,
  hasError = false,
  isLiking = false,
  copied = false,
  onBack,
  onOpenProfile,
  onLike,
  onShare,
  onRepeat,
  onCopyPrompt,
}: FeedDetailScreenProps) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (hasError || !feedItem) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-4">
        <p className="text-center text-muted-foreground">Запись не найдена</p>
        <button
          onClick={onBack}
          className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          Назад
        </button>
      </div>
    );
  }

  const displayName = feedItem.user.username
    ? `@${feedItem.user.username}`
    : feedItem.user.firstName || 'Пользователь';

  return (
    <div className="min-h-screen pb-20">
      <div className="sticky top-0 z-10 flex items-center gap-3 bg-background/80 p-4 backdrop-blur-lg">
        <button
          onClick={onBack}
          className="detail-back-button flex h-10 w-10 items-center justify-center rounded-full bg-secondary"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-semibold">Лента</h1>
          <p className="text-xs text-muted-foreground">
            {formatRelativeTime(feedItem.createdAt)}
          </p>
        </div>
      </div>

      <div className="p-4">
        <button
          onClick={onOpenProfile}
          className="mb-4 flex items-center gap-3 rounded-xl bg-secondary/50 p-3 transition-colors hover:bg-secondary"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
            <User className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 text-left">
            <p className="font-medium">{displayName}</p>
            <p className="text-xs text-muted-foreground">
              Использовал {feedItem.model.name}
            </p>
          </div>
        </button>

        <div className="relative aspect-square overflow-hidden rounded-2xl bg-secondary">
          {feedItem.resultUrl && (
            <>
              {feedItem.resultType === 'VIDEO' ? (
                <VideoPlayer
                  src={feedItem.resultUrl}
                  poster={feedItem.thumbnailUrl}
                  preload="metadata"
                  className="h-full w-full"
                  videoClassName="h-full w-full object-contain"
                />
              ) : feedItem.resultType === 'AUDIO' ? (
                <div className="relative flex h-full w-full flex-col justify-end overflow-hidden bg-[#17181a] p-4">
                  {feedItem.thumbnailUrl ? (
                    <S3Image
                      src={feedItem.thumbnailUrl}
                      alt={feedItem.prompt || 'Music cover'}
                      fill
                      className="object-cover opacity-50 blur-sm scale-105"
                      priority
                    />
                  ) : null}
                  <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/40 to-black/80" />
                  <div className="relative z-[1] mb-4 flex items-center gap-3">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white/10">
                      {feedItem.thumbnailUrl ? (
                        <S3Image
                          src={feedItem.thumbnailUrl}
                          alt={feedItem.prompt || 'Music cover'}
                          width={80}
                          height={80}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <Music className="h-7 w-7 text-white" strokeWidth={1.5} />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-white">
                        {feedItem.model.name}
                      </p>
                      <p className="text-xs text-white/65">
                        {feedItem.audioTracks && feedItem.audioTracks.length > 1
                          ? `${feedItem.audioTracks.length} варианта`
                          : 'Аудио'}
                      </p>
                    </div>
                  </div>
                  <div className="relative z-[1] space-y-2">
                    {(feedItem.audioTracks && feedItem.audioTracks.length > 0
                      ? feedItem.audioTracks
                      : [{ id: feedItem.id, audioUrl: feedItem.resultUrl, title: feedItem.model.name }]
                    ).map((track, index) => (
                      <audio
                        key={track.id || `${track.audioUrl}-${index}`}
                        className="w-full"
                        controls
                        preload="metadata"
                        src={track.audioUrl}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div
                  className="group relative h-full w-full cursor-pointer"
                  onClick={() => setIsPreviewOpen(true)}
                >
                  <S3Image
                    src={feedItem.resultUrl}
                    alt={feedItem.prompt || 'Generated image'}
                    fill
                    className="object-contain"
                    priority
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/20">
                    <div className="rounded-full bg-black/50 p-3 opacity-0 transition-opacity group-hover:opacity-100">
                      <Expand className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {feedItem.resultType === 'VIDEO' && (
            <div className="absolute bottom-3 left-3 flex items-center gap-1 rounded-full bg-black/50 px-2 py-1 text-xs text-white">
              <Play className="h-3 w-3" />
              Видео
            </div>
          )}
        </div>

        {feedItem.resultUrl && feedItem.resultType === 'IMAGE' && (
          <ImagePreviewModal
            isOpen={isPreviewOpen}
            onClose={() => setIsPreviewOpen(false)}
            src={feedItem.resultUrl}
            alt={feedItem.prompt || 'Generated image'}
            primaryAction={{
              label: feedItem.isPromptPrivate
                ? 'Создать с этой моделью'
                : 'Повторить',
              icon: RefreshCw,
              onClick: onRepeat,
            }}
          />
        )}

        <div className="mt-4 grid grid-cols-3 gap-2">
          <button
            onClick={onLike}
            disabled={isLiking}
            className="flex flex-col items-center gap-1 rounded-xl bg-secondary p-3 transition-colors hover:bg-secondary/80"
          >
            {isLiking ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Heart className={cn('h-5 w-5', feedItem.likesCount > 0 && 'fill-red-500 text-red-500')} />
            )}
            <span className="text-xs">{feedItem.likesCount} лайков</span>
          </button>
          <button
            onClick={onShare}
            className="flex flex-col items-center gap-1 rounded-xl bg-secondary p-3 transition-colors hover:bg-secondary/80"
          >
            <Share2 className="h-5 w-5" />
            <span className="text-xs">Поделиться</span>
          </button>
          <button
            onClick={onRepeat}
            className="flex flex-col items-center gap-1 rounded-xl bg-primary p-3 text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <RefreshCw className="h-5 w-5" />
            <span className="text-xs">Повторить</span>
          </button>
        </div>

        <div className="mt-6">
          <h2 className="text-sm font-semibold">Промпт</h2>
          {feedItem.isPromptPrivate ? (
            <div className="mt-2 flex items-center gap-2 rounded-xl bg-secondary/50 p-3 text-sm text-muted-foreground">
              <EyeOff className="h-4 w-4 flex-shrink-0" />
              <span>Автор скрыл промпт</span>
            </div>
          ) : feedItem.prompt ? (
            <div className="relative mt-2">
              <p className="rounded-xl bg-secondary p-3 pr-12 text-sm">
                {feedItem.prompt}
              </p>
              <button
                onClick={onCopyPrompt}
                className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg bg-background/50 transition-colors hover:bg-background"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4 text-muted-foreground" />
                )}
              </button>
            </div>
          ) : (
            <p className="mt-2 rounded-xl bg-secondary/50 p-3 text-sm text-muted-foreground">
              Промпт не указан
            </p>
          )}
        </div>

        {!feedItem.isPromptPrivate && feedItem.negativePrompt && (
          <div className="mt-4">
            <h2 className="text-sm font-semibold text-muted-foreground">
              Негативный промпт
            </h2>
            <p className="mt-2 rounded-xl bg-secondary/50 p-3 text-sm text-muted-foreground">
              {feedItem.negativePrompt}
            </p>
          </div>
        )}

        <div className="mt-6 space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Модель</span>
            <span className="font-medium">{feedItem.model.name}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Тип</span>
            <span className="font-medium">
              {feedItem.resultType === 'IMAGE'
                ? 'Изображение'
                : feedItem.resultType === 'VIDEO'
                  ? 'Видео'
                  : 'Аудио'}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Создано</span>
            <span className="font-medium">
              {new Date(feedItem.createdAt).toLocaleString('ru-RU')}
            </span>
          </div>
        </div>

        <button
          onClick={onRepeat}
          className="mt-6 w-full rounded-xl bg-primary py-4 text-center font-semibold text-primary-foreground"
        >
          {feedItem.isPromptPrivate ? 'Создать с этой моделью' : 'Повторить генерацию'}
        </button>
      </div>
    </div>
  );
}

function formatRelativeTime(date: string | Date): string {
  const value = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diff = now.getTime() - value.getTime();

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'только что';
  if (minutes < 60) return `${minutes} мин. назад`;
  if (hours < 24) return `${hours} ч. назад`;
  if (days < 7) return `${days} дн. назад`;

  return value.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
